import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toLocalYMD } from '../../../utils/dateUtils';
import api from '../../../utils/api';
import { huntService } from '../../features/services/huntService';
import { weatherService } from '../services/weatherService';
import { buildStandConditionRows } from '../utils/standSheetUtils';
import {
  mapClubsToStandOptions,
  isWhereStepReady,
  buildPersonalLogPayload,
  resolvePersonalLogCoordinates,
  togglePeriod,
} from '../utils/logHuntSheetUtils';
import { HuntLoggedDoneActions } from './HuntLoggedDoneActions';
import './SignOutModal.css';

const SIT_PERIODS = [
  { value: 'morning', label: 'MORNING', time: 'before 10a' },
  { value: 'midday', label: 'MIDDAY', time: '10a — 2p' },
  { value: 'afternoon', label: 'AFTERNOON', time: 'after 2p' },
];

const SAW_KEYS = ['Bucks', 'Does', 'Fawns'];
const HARVEST_OPTS = [
  { id: 'none', label: 'Nothing down — just a sit' },
  { id: 'buck', label: 'Buck' },
  { id: 'doe', label: 'Doe' },
];

const EMPTY_SAW = { Bucks: 0, Does: 0, Fawns: 0 };

/**
 * Personal / Hunts-page enamel log sheet — 4 steps (where → seen → harvest → conditions).
 */
export function LogHuntSheet({ show, onClose, onLogged }) {
  const [step, setStep] = useState('where');
  const [hDate, setHDate] = useState(() => toLocalYMD(new Date()));
  const [hGround, setHGround] = useState('private');
  const [hLocName, setHLocName] = useState('Private Spot');
  const [hStandId, setHStandId] = useState(null);
  const [hPeriods, setHPeriods] = useState([]);
  const [stands, setStands] = useState([]);
  const [standsLoading, setStandsLoading] = useState(false);
  const [saw, setSaw] = useState(EMPTY_SAW);
  const [harvest, setHarvest] = useState('none');
  const [hWeight, setHWeight] = useState('');
  const [hPoints, setHPoints] = useState('');
  const [notes, setNotes] = useState('');
  const [huntWeather, setHuntWeather] = useState(null);
  const [huntWeatherLoading, setHuntWeatherLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [finishLocked, setFinishLocked] = useState(false);

  const selectedStand = stands.find((s) => Number(s.id) === Number(hStandId)) || null;
  const whereReady = isWhereStepReady({
    date: hDate,
    ground: hGround,
    locationName: hLocName,
    huntLocationId: hStandId,
    timePeriods: hPeriods,
  });

  const resetAll = useCallback(() => {
    setStep('where');
    setHDate(toLocalYMD(new Date()));
    setHGround('private');
    setHLocName('Private Spot');
    setHStandId(null);
    setHPeriods([]);
    setSaw(EMPTY_SAW);
    setHarvest('none');
    setHWeight('');
    setHPoints('');
    setNotes('');
    setHuntWeather(null);
    setFormError('');
    setFinishLocked(false);
  }, []);

  useEffect(() => {
    if (!show) {
      resetAll();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setStandsLoading(true);
        const resp = await api.get('/clubs/');
        const raw = resp?.data;
        const clubs = Array.isArray(raw) ? raw : raw?.results || [];
        if (!cancelled) setStands(mapClubsToStandOptions(clubs));
      } catch (_) {
        if (!cancelled) setStands([]);
      } finally {
        if (!cancelled) setStandsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, resetAll]);

  useEffect(() => {
    if (!show || step !== 'cond' || !whereReady) {
      return undefined;
    }
    const coords = resolvePersonalLogCoordinates({
      ground: hGround,
      standGeojson: selectedStand?.geojson,
    });
    if (!coords?.lat || !coords?.lng) {
      setHuntWeather(null);
      return undefined;
    }
    let cancelled = false;
    setHuntWeatherLoading(true);
    Promise.resolve(
      weatherService.getHuntWeatherData(coords.lat, coords.lng, hDate, hPeriods)
    )
      .then((data) => {
        if (!cancelled) setHuntWeather(data || null);
      })
      .catch(() => {
        if (!cancelled) setHuntWeather(null);
      })
      .finally(() => {
        if (!cancelled) setHuntWeatherLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, step, whereReady, hGround, hDate, hPeriods, selectedStand?.geojson]);

  const handleClose = () => {
    const wasFinished = finishLocked;
    resetAll();
    onClose({ logged: wasFinished });
  };

  const handleSubmit = async () => {
    setFormError('');
    setSubmitting(true);
    try {
      const payload = buildPersonalLogPayload({
        date: hDate,
        timePeriods: hPeriods,
        ground: hGround,
        locationName: hGround === 'club' ? selectedStand?.name || hLocName : hLocName,
        clubId: selectedStand?.clubId,
        huntLocationId: hStandId,
        saw,
        harvest,
        weight: hWeight,
        points: hPoints,
        notes,
        weatherData: huntWeather || {},
      });
      const coords = resolvePersonalLogCoordinates({
        ground: hGround,
        standGeojson: selectedStand?.geojson,
      });
      await huntService.createHunt(payload, coords);
      setFinishLocked(true);
      setStep('done');
      if (typeof onLogged === 'function') {
        try {
          await onLogged();
        } catch (_) {}
      }
    } catch (err) {
      const apiMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        (typeof err?.response?.data === 'string' ? err.response.data : null);
      setFormError(apiMsg || err.message || 'Failed to log hunt');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const groundLabel = hGround === 'club' ? 'CLUB GROUND' : 'MY GROUND';
  const placeLabel =
    hGround === 'club' ? selectedStand?.name || 'Stand' : hLocName || 'Private Spot';
  const kicker = `${groundLabel} · PERSONAL LOG`;

  const stepMeta = {
    where: { label: 'LOG A HUNT · STEP 1 OF 4', pct: 25 },
    saw: { label: 'LOG A HUNT · STEP 2 OF 4', pct: 50 },
    harvest: { label: 'LOG A HUNT · STEP 3 OF 4', pct: 75 },
    cond: { label: 'LOG A HUNT · STEP 4 OF 4', pct: 100 },
  };

  const renderProgress = (key) => {
    const meta = stepMeta[key];
    if (!meta) return null;
    return (
      <div className="stand-tagin-sheet__progress">
        <span className="stand-tagin-sheet__progress-label">{meta.label}</span>
        <span className="stand-tagin-sheet__progress-track" aria-hidden>
          <span className="stand-tagin-sheet__progress-fill" style={{ width: `${meta.pct}%` }} />
        </span>
      </div>
    );
  };

  const condRows = buildStandConditionRows({
    standName: placeLabel,
    sitPeriod: hPeriods[0] || null,
    date: hDate,
    huntWeather,
  });
  // Show all selected periods' weather when multi-period
  if (huntWeather && hPeriods.length > 1) {
    hPeriods.forEach((p) => {
      const pw = huntWeather[p];
      if (!pw) return;
      condRows.push({
        k: String(p).toUpperCase(),
        v: [
          pw.temperature != null ? `${Math.round(pw.temperature)}°F` : null,
          pw.wind_speed != null ? `${Math.round(pw.wind_speed)} mph` : null,
          pw.wind_direction_text || null,
          pw.description || null,
        ]
          .filter(Boolean)
          .join(' · '),
      });
    });
  }

  const showHarvestFields = harvest === 'buck' || harvest === 'doe';

  const renderWhere = () => (
    <>
      {renderProgress('where')}
      <div className="stand-tagin-sheet__headline">When and where did you sit?</div>

      <div className="stand-tagin-sheet__field" style={{ marginTop: 0 }}>
        <div className="stand-tagin-sheet__label">DATE</div>
        <input
          className="stand-tagin-sheet__input"
          type="date"
          value={hDate}
          onChange={(e) => setHDate(e.target.value)}
          aria-label="Hunt date"
          data-testid="log-hunt-date"
        />
      </div>

      <div className="stand-tagin-sheet__field">
        <div className="stand-tagin-sheet__label">GROUND</div>
        <div className="stand-tagin-sheet__ground" role="group" aria-label="Ground type">
          {[
            { id: 'club', label: 'CLUB GROUND' },
            { id: 'private', label: 'MY GROUND' },
          ].map((g) => (
            <button
              key={g.id}
              type="button"
              className={`stand-tagin-sheet__ground-btn${hGround === g.id ? ' is-active' : ''}`}
              aria-pressed={hGround === g.id}
              onClick={() => {
                setHGround(g.id);
                setHStandId(null);
                setHLocName(g.id === 'private' ? 'Private Spot' : '');
              }}
              data-testid={`log-hunt-ground-${g.id}`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="stand-tagin-sheet__field">
        <div className="stand-tagin-sheet__label">
          {hGround === 'club' ? 'WHICH STAND' : 'WHICH PLACE'}
        </div>
        {hGround === 'club' ? (
          standsLoading ? (
            <div className="stand-tagin-sheet__note">Loading stands…</div>
          ) : stands.length === 0 ? (
            <div className="stand-tagin-sheet__note">No club stands found. Switch to My Ground.</div>
          ) : (
            <div className="stand-tagin-sheet__chips" role="group" aria-label="Stand">
              {stands.map((s) => {
                const on = Number(hStandId) === Number(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`stand-tagin-sheet__chip${on ? ' is-active' : ''}`}
                    aria-pressed={on}
                    onClick={() => {
                      setHStandId(s.id);
                      setHLocName(s.name);
                    }}
                    data-testid={`log-hunt-stand-${s.id}`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          )
        ) : (
          <input
            className="stand-tagin-sheet__input"
            value={hLocName}
            onChange={(e) => setHLocName(e.target.value)}
            placeholder="Private Spot"
            aria-label="Place name"
            data-testid="log-hunt-place"
          />
        )}
      </div>

      <div className="stand-tagin-sheet__field">
        <div className="stand-tagin-sheet__label">TIME OF DAY · PICK ANY</div>
        <div className="stand-tagin-sheet__periods" role="group" aria-label="Time periods">
          {SIT_PERIODS.map((p) => {
            const on = hPeriods.includes(p.value);
            return (
              <button
                key={p.value}
                type="button"
                className={`stand-tagin-sheet__period${on ? ' is-active' : ''}`}
                aria-pressed={on}
                onClick={() => setHPeriods((prev) => togglePeriod(prev, p.value))}
                data-testid={`log-hunt-period-${p.value}`}
              >
                <span className="stand-tagin-sheet__period-label">{p.label}</span>
                <span className="stand-tagin-sheet__period-time">{p.time}</span>
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className={`stand-tagin-sheet__cta${whereReady ? ' is-ready' : ''}`}
        disabled={!whereReady}
        onClick={() => setStep('saw')}
        data-testid="log-hunt-to-saw"
      >
        {whereReady ? 'NEXT · WHAT DID YOU SEE' : 'PICK DATE, PLACE & SIT'}
      </button>
    </>
  );

  const renderSaw = () => (
    <>
      {renderProgress('saw')}
      <div className="stand-tagin-sheet__headline">What&apos;d you see?</div>
      <div className="stand-tagin-sheet__counters">
        {SAW_KEYS.map((key) => (
          <div key={key} className="stand-tagin-sheet__counter">
            <span className="stand-tagin-sheet__counter-label">{key}</span>
            <button
              type="button"
              className="stand-tagin-sheet__counter-btn"
              aria-label={`Decrease ${key}`}
              onClick={() =>
                setSaw((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) }))
              }
            >
              –
            </button>
            <span className="stand-tagin-sheet__counter-value">{saw[key] || 0}</span>
            <button
              type="button"
              className="stand-tagin-sheet__counter-btn is-plus"
              aria-label={`Increase ${key}`}
              onClick={() => setSaw((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }))}
            >
              +
            </button>
          </div>
        ))}
      </div>
      <div className="stand-tagin-sheet__nav-row">
        <button type="button" className="stand-tagin-sheet__secondary" onClick={() => setStep('where')}>
          BACK
        </button>
        <button
          type="button"
          className="stand-tagin-sheet__cta is-ready is-loden stand-tagin-sheet__cta--flex"
          onClick={() => setStep('harvest')}
          data-testid="log-hunt-to-harvest"
        >
          NEXT · HARVEST
        </button>
      </div>
    </>
  );

  const renderHarvest = () => (
    <>
      {renderProgress('harvest')}
      <div className="stand-tagin-sheet__headline">Put one down?</div>
      <div className="stand-tagin-sheet__harvest-opts">
        {HARVEST_OPTS.map((opt) => {
          const on = harvest === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              className={`stand-tagin-sheet__harvest-opt${on ? ' is-active' : ''}`}
              aria-pressed={on}
              onClick={() => setHarvest(opt.id)}
              data-testid={`log-hunt-harvest-${opt.id}`}
            >
              <span className={`stand-tagin-sheet__harvest-dot${on ? ' is-on' : ''}`} />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
      {showHarvestFields && (
        <div className="stand-tagin-sheet__harvest-fields">
          <div>
            <div className="stand-tagin-sheet__label">FIELD DRESSED</div>
            <input
              className="stand-tagin-sheet__input"
              value={hWeight}
              onChange={(e) => setHWeight(e.target.value)}
              placeholder="lb"
              inputMode="decimal"
            />
          </div>
          {harvest === 'buck' && (
            <div>
              <div className="stand-tagin-sheet__label">POINTS</div>
              <input
                className="stand-tagin-sheet__input"
                value={hPoints}
                onChange={(e) => setHPoints(e.target.value)}
                placeholder="8"
                inputMode="numeric"
              />
            </div>
          )}
        </div>
      )}
      <div className="stand-tagin-sheet__nav-row">
        <button type="button" className="stand-tagin-sheet__secondary" onClick={() => setStep('saw')}>
          BACK
        </button>
        <button
          type="button"
          className="stand-tagin-sheet__cta is-ready is-loden stand-tagin-sheet__cta--flex"
          onClick={() => setStep('cond')}
          data-testid="log-hunt-to-cond"
        >
          NEXT · CONDITIONS
        </button>
      </div>
    </>
  );

  const renderCond = () => (
    <>
      {renderProgress('cond')}
      <div className="stand-tagin-sheet__headline">Conditions, pulled for you</div>
      <p className="stand-tagin-sheet__subcopy">
        Recorded for your sit. Notes go in the book below.
      </p>
      {huntWeatherLoading ? (
        <div className="stand-tagin-sheet__note">Pulling weather…</div>
      ) : (
        <div className="stand-tagin-sheet__cond" data-testid="log-hunt-conditions">
          {condRows.map((row) => (
            <div key={`${row.k}-${row.v}`} className="stand-tagin-sheet__cond-row">
              <span className="stand-tagin-sheet__cond-k">{row.k}</span>
              <span className="stand-tagin-sheet__cond-v">{row.v}</span>
            </div>
          ))}
        </div>
      )}
      <div className="stand-tagin-sheet__field">
        <div className="stand-tagin-sheet__label">NOTES FOR THE BOOK</div>
        <textarea
          className="stand-tagin-sheet__textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Two does at the pinch point right at last light."
          rows={3}
        />
      </div>
      <div className="stand-tagin-sheet__nav-row">
        <button
          type="button"
          className="stand-tagin-sheet__secondary"
          onClick={() => setStep('harvest')}
          disabled={submitting}
        >
          BACK
        </button>
        <button
          type="button"
          className="stand-tagin-sheet__cta is-ready stand-tagin-sheet__cta--flex"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="log-hunt-submit"
        >
          {submitting ? 'SAVING…' : 'PUT IT IN THE BOOKS'}
        </button>
      </div>
    </>
  );

  const renderDone = () => {
    const seen = (saw.Bucks || 0) + (saw.Does || 0) + (saw.Fawns || 0);
    return (
      <div className="stand-tagin-sheet__done" data-testid="log-hunt-done">
        <div className="stand-tagin-sheet__stamp">IN THE BOOKS</div>
        <p className="stand-tagin-sheet__done-line">
          {placeLabel} · {hDate} · {seen} deer seen
          {harvest !== 'none' ? ` · ${harvest}` : ''}
        </p>
        <HuntLoggedDoneActions
          harvested={harvest !== 'none'}
          closeLabel="BACK TO MY HUNTS"
          closeTestId="log-hunt-done-close"
          onClose={handleClose}
        />
      </div>
    );
  };

  let body = null;
  if (step === 'done') body = renderDone();
  else if (step === 'where') body = renderWhere();
  else if (step === 'saw') body = renderSaw();
  else if (step === 'harvest') body = renderHarvest();
  else if (step === 'cond') body = renderCond();

  return createPortal(
    <div
      className="stand-tagin-backdrop"
      data-testid="log-hunt-sheet"
      role="presentation"
      onClick={handleClose}
    >
      <div
        className="stand-tagin-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="log-hunt-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="stand-tagin-sheet__header">
          <span className="stand-tagin-sheet__badge is-taken" aria-hidden>
            ＋
          </span>
          <span className="stand-tagin-sheet__titles">
            <span id="log-hunt-title" className="stand-tagin-sheet__name">
              {step === 'where' ? 'New hunt' : placeLabel}
            </span>
            <span className="stand-tagin-sheet__kicker">{kicker}</span>
          </span>
          <button
            type="button"
            className="stand-tagin-sheet__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="stand-tagin-sheet__body">
          {formError && step !== 'done' ? (
            <div className="stand-tagin-sheet__error" role="alert">
              {formError}
            </div>
          ) : null}
          {body}
        </div>
      </div>
    </div>,
    document.body
  );
}
